import { Observable } from "rxjs";
import { EntityId, IMyTask } from ".";
import {
  Circulation,
  CirculationId,
  ICirculationInit,
  ICirculationTemplate,
  IMetaData,
  IStartMultipleCirculationsResult,
  prepareId
} from "./circulation";
import {
  IDocument,
  IDocumentUploadEndpoints,
  IHyperlinkToSave
} from "./document";
import {
  ICirculationTemplateStep,
  IReorderStepsData,
  IStepForwardData,
  IStepInit,
  IStepOverrideForwardData,
  IStepResultData,
  Step,
  StepId
} from "./step";

/**
 * Service to work with cirulations
 */
export abstract class AbstractCirculationsService {
  public abstract overrideForwardStep(
    data: IStepOverrideForwardData
  ): Observable<void>;
  /**
   * Creates a new circulation and applies the template
   * @param circulation The circulation to cancel
   */
  public abstract ApplyTemplate(
    template: ICirculationTemplate
  ): Observable<CirculationId>;

  /**
   * Opens a Document in a new Tab
   * @param doc document that should open
   */
  public abstract openDocument(circId: EntityId, doc: IDocument): void;

  /**
   * Cancel the circulation and all open steps
   * @param circulation The circulation to cancel
   */
  public abstract CancelCirculation(circulation: Circulation): Observable<void>;

  /**
   *
   * @param id
   */
  public abstract DeleteTemplate(
    template: ICirculationTemplate
  ): Observable<void>;

  /**
   * Complete one step and start the next.
   * @param step The step to complete
   */
  public abstract CompleteStep(
    step: Step,
    result: IStepResultData
  ): Observable<void>;

  /**
   * Creates a new Circulation from a prepared-id
   * @param id The previously prepared settings-id
   */
  public abstract CreateNewFromPrepared(
    id: prepareId
  ): Observable<CirculationId>;

  /**
   * Deletes a step from it's Circulation
   * @param step The step to delete
   */
  public abstract DeleteStep(step: IStepInit): Observable<CirculationId>;

  /**
   * Forward a step to a different user.
   * @param data the forwarding-data
   */
  public abstract ForwardStep(data: IStepForwardData): Observable<void>;

  /**
   * Retrieve a single circulation
   * @param id the id of the circulation
   */
  public abstract GetCirculation(id: CirculationId): Observable<Circulation>;

  /**
   * Get all circulations, the current user has started
   * @param onlyRunning if true, only currently running circulations are returned
   */
  public abstract GetCirculations(
    onlyRunning: boolean
  ): Observable<Circulation[]>;

  /**
   * Adds a new Hyperlink to a circulation.
   * @param doc metadata of the hyperlink.
   * @param circId the id of the circulation.
   * @param stepId the current step in which the hyperlink was added.
   */
  public abstract AddHyperlink(
    doc: IHyperlinkToSave,
    circId: CirculationId,
    stepNo?: number
  ): Observable<IDocument>;

  /**
   * Retrieves the documents for a circulation
   * @param circId the id of the circulation
   * @param docId the id of the document
   */
  public abstract DownloadDocument(
    circId: CirculationId,
    docId: string
  ): Observable<Blob>;

  /**
   * Retrieves the documents for a circulation
   * @param id the id of the circulation
   */
  public abstract GetDocuments(id: CirculationId): Observable<IDocument[]>;

  /**
   * Retrieves the urls for document-upload
   * @param id the id of the circulation
   */
  public abstract GetDocumentUploadEndpoints(
    id: CirculationId,
    stepNo: number
  ): Observable<IDocumentUploadEndpoints>;

  /**
   * Retrieves all non-completed steps for the current user
   */
  public abstract GetMyTasks(): Observable<IMyTask[]>;

  /**
   * Retrieves all editable templates for the current user
   */
  public abstract GetMyTemplates(): Observable<ICirculationTemplate[]>;

  /**
   * Get one step of a circulation
   * @param id The Circulation id
   * @param stepId The Step id
   */
  public abstract GetStep(id: CirculationId, stepId: StepId): Observable<Step>;

  /**
   * Get all steps of a circulation
   * @param id The Circulation id
   */
  public abstract GetSteps(id: CirculationId): Observable<Step[]>;

  /**
   * Retrieves one template
   */
  public abstract GetTemplate(
    id: CirculationId
  ): Observable<ICirculationTemplate>;

  /**
   * Retrieves all readable/usable templates for the current user
   */
  public abstract GetTemplates(): Observable<ICirculationTemplate[]>;

  /**
   * Removes the document.
   * @param circId The Circulation id
   * @param docId The id of the document to remove
   */
  public abstract RemoveDocument(
    circId: CirculationId,
    docId: string
  ): Observable<void>;

  /**
   * Re-Orders the steps given
   * @param reorderData the re-oder data
   */
  public abstract ReorderSteps(
    reorderData: IReorderStepsData
  ): Observable<CirculationId>;

  /**
   * Save changes to a cirulation
   * @param circulation the modified circulation
   */
  public abstract SaveCirculation(
    circulation: Circulation
  ): Observable<CirculationId>;

  /**
   * Copy the cirulation
   * @param circId the circ to be copied
   */
  public abstract CopyCirculation(circId: EntityId): Observable<CirculationId>;

  /**
   * Save changes to a template
   * @param template the modified template
   */
  public abstract SaveCirculationTemplate(
    template: ICirculationTemplate
  ): Observable<CirculationId>;

  /**
   * Save changes to a step
   * @param step the modified step
   */
  public abstract SaveStep(step: Step): Observable<StepId>;

  /**
   * Delete target Step
   * @param step step to delete
   */
  public abstract DeleteTemplateStep(
    step: ICirculationTemplateStep,
    template: ICirculationTemplate
  ): Observable<void>;

  /**
   * Delete target metadata from template
   */
  public abstract DeleteTemplateData(
    data: IMetaData,
    template: ICirculationTemplate
  ): Observable<void>;

  /**
   * Start a circulation (i.e. set it's state to running) and start the first step.
   * @param circulation the circulation
   */
  public abstract StartCirculation(circulation: Circulation): Observable<void>;

  /**
   * Start multiple circulations
   * @param circulations the circulations to start
   */
  public abstract StartMultipleCirculations(
    circulations: ICirculationInit[]
  ): Observable<IStartMultipleCirculationsResult>;
}
